import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'

// Screens "no screens yet" empty state — mirrors the groups "no group"
// illustration (soft blob + three offset stacked cards) so the two surfaces
// read as one system. Shared by the Dashboard's My Screens tab and the
// Screens page so both show the identical, richer empty state instead of a
// plain single-icon tile. `hasSearch` collapses to a terse "no data" line
// when the emptiness is just a filtered-out list rather than a fresh account.
export default function ScreensEmptyState({
  onAdd, hasSearch = false, addLabel,
}: {
  // Optional: view-only roles (no screens:create) get the empty state without
  // the pair-a-screen CTA.
  onAdd?: () => void
  hasSearch?: boolean
  addLabel?: string
}) {
  const { t } = useTranslation()
  if (hasSearch) {
    return (
      <div className="flex items-center justify-center text-center min-h-[40vh] text-muted-foreground">
        <p className="text-sm">{t('common.noData', 'No data found')}</p>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center text-center min-h-[45vh] gap-3 py-10">
      {/* Stacked-screens illustration — same composition as GroupsEmptyState,
          but each card carries a little 16:9 "display" glyph. */}
      <div className="relative w-44 h-32">
        <div className="absolute inset-0 rounded-full bg-muted/40" />
        <div className="absolute top-3 start-4 w-32 h-8 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-6 h-4 rounded-sm border border-muted-foreground/30 bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
        <div className="absolute top-12 start-10 w-32 h-8 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-6 h-4 rounded-sm border border-muted-foreground/30 bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
        <div className="absolute top-[5.5rem] start-4 w-32 h-8 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-6 h-4 rounded-sm border border-muted-foreground/30 bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
      </div>
      <h3 className="text-base font-semibold text-foreground">
        {t('dashboard.screensEmptyTitle', 'No screens registered yet')}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md">
        {t('dashboard.screensEmptyDesc', 'Pair your first screen with the WeTook player app to start scheduling content.')}
      </p>
      {onAdd && (
        <Button onClick={onAdd} className="gap-2 mt-2">
          <Plus className="w-4 h-4" />
          {addLabel ?? t('dashboard.addNewScreen', 'Add New Screen')}
        </Button>
      )}
    </div>
  )
}
